// Accept Invitation Page strings — EN + TR.
//
// Wave-5 Day 1. AcceptInvitationPage (`features/onboarding/AcceptInvitationPage.tsx`)
// chrome localization. PUBLIC route (no auth) — token validation 5-state UX
// + password set form + 4 error screens + already-accepted state.
//
// AUDIT TOP-10 #3 closure: page was pure-EN; yeni davetli Hans BO3 Berlin /
// Mehmet SO2 / Aybars SO3 Accept-Language TR olsa bile first impression EN
// idi → bilingual.
//
// 🔴 SELLER-LEAK INVARIANT: NO vendor names. ORG_TYPE_LABELS + SCOPE_LABELS
// gelir from mocks/data/onboarding/invitations module (role-type labels,
// platform-neutral). This dict covers only page-chrome wording.

import type { LocalizedDict } from '../index'

type AcceptInvitationStrings = {
  brand: string
  brandTagline: string
  loadingState: {
    message: string
  }
  errorStates: {
    genericTitle: string
    genericMessage: (reason: string) => string
    invalidTitle: string
    invalidMessage: string
    expiredTitle: string
    expiredMessage: string
    revokedTitle: string
    revokedMessage: string
    homeLink: string
  }
  alreadyAccepted: {
    title: string
    description: string
    homeLink: string
  }
  welcomeForm: {
    title: string
    invitedToJoin: (orgName: string, role: string) => string
    expiresAt: (date: string) => string
    passwordLabel: string
    passwordPlaceholder: string
    passwordTooShort: (min: number) => string
    confirmPasswordLabel: string
    confirmPasswordPlaceholder: string
    passwordMismatch: string
    showPasswordAria: string
    hidePasswordAria: string
    termsAccept: string
    termsLink: string
    privacyLink: string
    termsRequired: string
    activateButton: string
    activatingInProgress: string
    activateFooter: (orgName: string) => string
  }
  realMode: {
    title: string
    subtitle: string
    displayNameLabel: string
    displayNamePlaceholder: string
    submitButton: string
    submitting: string
    errors: {
      expired: string
      revoked: string
      invalid: string
      alreadyUsed: string
      forbidden: string
      rateLimited: string
      generic: (reason: string) => string
    }
  }
}

export const ACCEPT_INVITATION_STRINGS: LocalizedDict<AcceptInvitationStrings> = {
  en: {
    brand: 'PEXIFY',
    brandTagline: 'privately governed commerce ecosystem',
    loadingState: {
      message: 'Validating your invitation…',
    },
    errorStates: {
      genericTitle: 'Something went wrong',
      genericMessage: (reason) => `Couldn't validate your invitation: ${reason}`,
      invalidTitle: 'Invalid invitation link',
      invalidMessage:
        "This invitation token doesn't exist. Please check the link in your email or ask the person who invited you for a fresh link.",
      expiredTitle: 'Invitation expired',
      expiredMessage:
        'This invitation has expired (default 7-day window). Please ask the person who invited you to extend it or send a fresh invitation.',
      revokedTitle: 'Invitation revoked',
      revokedMessage:
        'This invitation has been cancelled by the admin. If you believe this is a mistake, contact the person who invited you.',
      homeLink: 'PEXIFY home',
    },
    alreadyAccepted: {
      title: 'Already accepted',
      description:
        'This invitation has already been used to create an account. Real login flow ships with Stage 5 backend resume — for now, return to the PEXIFY home page.',
      homeLink: 'Return to PEXIFY home',
    },
    welcomeForm: {
      title: 'Welcome to PEXIFY',
      invitedToJoin: (orgName, role) =>
        `You've been invited to join ${orgName} as ${role}.`,
      expiresAt: (date) => `Expires ${date}`,
      passwordLabel: 'Choose a password *',
      passwordPlaceholder: 'At least 8 characters',
      passwordTooShort: (min) => `Password must be at least ${min} characters`,
      confirmPasswordLabel: 'Confirm password *',
      confirmPasswordPlaceholder: 'Re-enter password',
      passwordMismatch: "Passwords don't match",
      showPasswordAria: 'Show password',
      hidePasswordAria: 'Hide password',
      termsAccept: 'I accept the PEXIFY',
      termsLink: 'Terms of Service',
      privacyLink: 'Privacy Policy',
      termsRequired: 'You must accept the Terms of Service to continue.',
      activateButton: 'Activate Account',
      activatingInProgress: 'Activating your account…',
      activateFooter: (orgName) =>
        `By clicking Activate, your ${orgName} organization will be created and you'll be logged in.`,
    },
    realMode: {
      title: 'Set up your account',
      subtitle:
        'You were invited to PEXIFY. Choose a display name and password to activate your account, then sign in.',
      displayNameLabel: 'Display name *',
      displayNamePlaceholder: 'Your name',
      submitButton: 'Activate & continue',
      submitting: 'Activating…',
      errors: {
        expired: 'This invitation has expired. Ask the person who invited you to send a fresh one.',
        revoked: 'This invitation was cancelled. Contact the person who invited you.',
        invalid: "This invitation link isn't valid. Check the link in your email.",
        alreadyUsed: 'This invitation was already used. Please sign in instead.',
        forbidden: 'This invitation cannot be accepted on this account type.',
        rateLimited: 'Too many attempts. Please wait a minute and try again.',
        generic: (reason) => `Could not activate your account: ${reason}`,
      },
    },
  },
  tr: {
    brand: 'PEXIFY',
    brandTagline: 'özel yönetilen ticaret ekosistemi',
    loadingState: {
      message: 'Davetin doğrulanıyor…',
    },
    errorStates: {
      genericTitle: 'Bir şeyler ters gitti',
      genericMessage: (reason) => `Davetin doğrulanamadı: ${reason}`,
      invalidTitle: 'Geçersiz davet bağlantısı',
      invalidMessage:
        'Bu davet token bulunamadı. E-postandaki bağlantıyı kontrol et veya seni davet eden kişiden yeni bir bağlantı iste.',
      expiredTitle: 'Davet süresi dolmuş',
      expiredMessage:
        'Bu davet süresi dolmuş (varsayılan 7 gün). Seni davet eden kişiden süreyi uzatmasını veya yeni bir davet göndermesini iste.',
      revokedTitle: 'Davet iptal edildi',
      revokedMessage:
        'Bu davet admin tarafından iptal edildi. Bunun bir hata olduğunu düşünüyorsan seni davet eden kişiyle iletişime geç.',
      homeLink: 'PEXIFY anasayfa',
    },
    alreadyAccepted: {
      title: 'Zaten kabul edildi',
      description:
        "Bu davet zaten kullanılmış ve hesap oluşturulmuş. Gerçek giriş akışı Stage 5 backend resume ile devreye girecek — şimdilik PEXIFY anasayfasına dön.",
      homeLink: "PEXIFY anasayfasına dön",
    },
    welcomeForm: {
      title: "PEXIFY'a hoş geldin",
      invitedToJoin: (orgName, role) =>
        `${orgName} organizasyonuna ${role} olarak davet edildin.`,
      expiresAt: (date) => `Bitiş ${date}`,
      passwordLabel: 'Bir şifre belirle *',
      passwordPlaceholder: 'En az 8 karakter',
      passwordTooShort: (min) => `Şifre en az ${min} karakter olmalı`,
      confirmPasswordLabel: 'Şifreyi onayla *',
      confirmPasswordPlaceholder: 'Şifreyi tekrar gir',
      passwordMismatch: 'Şifreler eşleşmiyor',
      showPasswordAria: 'Şifreyi göster',
      hidePasswordAria: 'Şifreyi gizle',
      termsAccept: "PEXIFY'in",
      termsLink: 'Kullanım Şartları',
      privacyLink: 'Gizlilik Politikası',
      termsRequired: 'Devam etmek için Kullanım Şartlarını kabul etmelisin.',
      activateButton: 'Hesabı Etkinleştir',
      activatingInProgress: 'Hesabın etkinleştiriliyor…',
      activateFooter: (orgName) =>
        `Etkinleştir'e tıkladığında ${orgName} organizasyonu oluşturulacak ve giriş yapacaksın.`,
    },
    realMode: {
      title: 'Hesabını oluştur',
      subtitle:
        "PEXIFY'a davet edildin. Hesabını etkinleştirmek için bir görünen ad ve şifre belirle, sonra giriş yap.",
      displayNameLabel: 'Görünen ad *',
      displayNamePlaceholder: 'Adın',
      submitButton: 'Etkinleştir ve devam et',
      submitting: 'Etkinleştiriliyor…',
      errors: {
        expired: 'Bu davetin süresi dolmuş. Seni davet eden kişiden yeni bir davet iste.',
        revoked: 'Bu davet iptal edildi. Seni davet eden kişiyle iletişime geç.',
        invalid: 'Bu davet bağlantısı geçerli değil. E-postandaki bağlantıyı kontrol et.',
        alreadyUsed: 'Bu davet zaten kullanılmış. Lütfen giriş yap.',
        forbidden: 'Bu davet bu hesap tipinde kabul edilemiyor.',
        rateLimited: 'Çok fazla deneme. Lütfen bir dakika bekleyip tekrar dene.',
        generic: (reason) => `Hesabın etkinleştirilemedi: ${reason}`,
      },
    },
  },
}
